// The assessment's forecast points: copied from the event's own locations, or
// entered by hand as lat/long or an OS grid reference.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PlaceIcon from "@mui/icons-material/Place";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_BASE_URL } from "../config";
import { osGridRefToLatLng, parseLatLng } from "../geo";
import { useFullScreenDialog } from "../useFullScreenDialog";
import SafetySection from "./SafetySection";
import { SAFETY_API, fmtPointWindow } from "./format";

const LOCATIONS_API = `${API_BASE_URL}/locations`;

export default function PointsSection({ assessment, onChange }) {
  const decided = assessment.status === "Decided";
  const [editing, setEditing] = useState(null);   // null | "new" | point
  const [picking, setPicking] = useState(false);

  const remove = (p) => {
    axios.delete(`${SAFETY_API}/points/${p.id}`).then(res => onChange(res.data)).catch(() => {});
  };

  return (
    <SafetySection id="points" title={`Points (${assessment.points.length})`}
      subtitle={<>Where forecasts are pulled for. Give a point's ground height so temperatures can be
        corrected from the forecast model's smoothed terrain.</>}
      actions={
        <Stack direction="row" spacing={1}>
          <Button startIcon={<PlaceIcon />} onClick={() => setPicking(true)} disabled={decided}>
            From event locations
          </Button>
          <Button startIcon={<AddIcon />} onClick={() => setEditing("new")} disabled={decided}>
            Add point
          </Button>
        </Stack>}
      summary={<PointsSummary points={assessment.points} />}>
      {assessment.points.length === 0 ? (
        <Alert severity="info">No points yet.</Alert>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Lat, long</TableCell>
              <TableCell>Elevation</TableCell>
              <TableCell>When people are here</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {assessment.points.map((p, n) => (
              <TableRow key={p.id}>
                <TableCell>
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ mr: 1 }}>{n + 1}.</Typography>
                  {p.name}
                  {p.event_location_id && (
                    <Tooltip title="Copied from an event location">
                      <PlaceIcon fontSize="inherit" color="action" sx={{ ml: 0.5, verticalAlign: "middle" }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{p.latitude.toFixed(5)}, {p.longitude.toFixed(5)}</TableCell>
                <TableCell>
                  {p.elevation_m != null ? `${Math.round(p.elevation_m)} m` : (
                    <Typography variant="body2" color="text.secondary" component="span">not set</Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {/* A route point is judged over when the field passes it; any
                      other point over the assessment's whole window. */}
                  {fmtPointWindow(p.window_start, p.window_end) || (
                    <Typography variant="body2" color="text.secondary" component="span">whole window</Typography>
                  )}
                  {p.distance_km != null && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      km {p.distance_km.toFixed(1)} on the route
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                  <IconButton size="small" onClick={() => setEditing(p)} disabled={decided} aria-label="Edit point">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => remove(p)} disabled={decided} aria-label="Remove point">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <PointDialog open={editing !== null} point={editing === "new" ? null : editing}
                   assessmentId={assessment.id} onClose={() => setEditing(null)}
                   onSaved={(next) => { onChange(next); setEditing(null); }} />
      <LocationPicker open={picking} assessment={assessment} onClose={() => setPicking(false)}
                      onSaved={(next) => { onChange(next); setPicking(false); }} />
    </SafetySection>
  );
}

// Collapsed: one line a point — name, height, when people are there.
function PointsSummary({ points }) {
  if (!points.length) return <Typography variant="body2" color="text.secondary">No points yet.</Typography>;
  return (
    <Box component="ol" sx={{ m: 0, pl: 3, columnWidth: 340, columnGap: 4 }}>
      {points.map(p => (
        <Typography component="li" variant="body2" key={p.id} sx={{ breakInside: "avoid" }}>
          {p.name}
          <Typography component="span" variant="caption" color="text.secondary">
            {p.elevation_m != null && ` · ${Math.round(p.elevation_m)} m`}
            {` · ${fmtPointWindow(p.window_start, p.window_end) || "whole window"}`}
          </Typography>
        </Typography>
      ))}
    </Box>
  );
}

function PointDialog({ open, point, assessmentId, onClose, onSaved }) {
  const fullScreen = useFullScreenDialog();
  const [form, setForm] = useState({ name: "", position: "", elevation: "" });

  useEffect(() => {
    if (!open) return;
    setForm(point
      ? { name: point.name, position: `${point.latitude}, ${point.longitude}`,
          elevation: point.elevation_m ?? "" }
      : { name: "", position: "", elevation: "" });
  }, [open, point]);

  // Either "lat, long" (decimal or DMS) or an OS grid reference.
  const coords = useMemo(() => {
    const s = form.position.trim();
    if (!s) return null;
    const [lat, lng] = parseLatLng(s);
    if (lat != null) return [lat, lng];
    return osGridRefToLatLng(s);
  }, [form.position]);

  const elevation = form.elevation === "" ? null : Number(form.elevation);
  const elevationOk = elevation === null || Number.isFinite(elevation);
  const valid = form.name.trim() && coords && elevationOk;
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = () => {
    const body = { name: form.name.trim(), latitude: coords[0], longitude: coords[1], elevation_m: elevation };
    const req = point
      ? axios.put(`${SAFETY_API}/points/${point.id}`, body)
      : axios.post(`${SAFETY_API}/assessments/${assessmentId}/points`, body);
    req.then(res => onSaved(res.data)).catch(() => {});
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>{point ? "Edit point" : "Add point"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Name" value={form.name} onChange={set("name")} autoFocus
                     placeholder="e.g. Crib Goch, Pen y Pass checkpoint" />
          <TextField label="Position" value={form.position} onChange={set("position")}
                     placeholder="53.0685, -4.0763  or  SH 60945 54370"
                     error={!!form.position.trim() && !coords}
                     helperText={form.position.trim() && !coords ? "Not a lat, long or OS grid reference."
                       : coords ? `${coords[0].toFixed(5)}, ${coords[1].toFixed(5)}` : "Lat, long or OS grid reference."} />
          <TextField label="Ground elevation (m)" value={form.elevation} onChange={set("elevation")}
                     type="number" error={!elevationOk}
                     helperText="Optional. Used to correct forecast temperatures to the point's height." />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!valid}>{point ? "Save" : "Add"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function LocationPicker({ open, assessment, onClose, onSaved }) {
  const fullScreen = useFullScreenDialog();
  const [locations, setLocations] = useState([]);
  const [chosen, setChosen] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    setChosen(new Set());
    axios.get(`${LOCATIONS_API}/`, { params: { event_id: assessment.event_id } })
      .then(res => setLocations(res.data || []))
      .catch(() => setLocations([]));
  }, [open, assessment.event_id]);

  const have = new Set(assessment.points.map(p => p.event_location_id).filter(Boolean));
  const usable = locations.filter(l => l.latitude != null && l.longitude != null);
  const noCoords = locations.length - usable.length;
  const toggle = (id) => setChosen(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const save = () => {
    axios.post(`${SAFETY_API}/assessments/${assessment.id}/points/from-locations`,
               { location_ids: [...chosen] })
      .then(res => onSaved(res.data)).catch(() => {});
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle>Add event locations</DialogTitle>
      <DialogContent>
        {usable.length === 0 ? (
          <Alert severity="info">
            This event has no locations with coordinates. Add them on the event's Locations, or add a
            point by hand.
          </Alert>
        ) : (
          <Stack>
            {usable.map(l => (
              <FormControlLabel key={l.id}
                control={<Checkbox checked={have.has(l.id) || chosen.has(l.id)} disabled={have.has(l.id)}
                                   onChange={() => toggle(l.id)} />}
                label={
                  <span>
                    {l.name}{l.type ? ` — ${l.type}` : ""}
                    {have.has(l.id) && <Typography component="span" variant="caption" color="text.secondary"> (added)</Typography>}
                  </span>
                } />
            ))}
          </Stack>
        )}
        {noCoords > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
            {noCoords} location{noCoords === 1 ? " has" : "s have"} no coordinates and can't be used.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!chosen.size}>Add {chosen.size || ""}</Button>
      </DialogActions>
    </Dialog>
  );
}
